import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Phone, 
  Clock, 
  AlertCircle, 
  Calendar, 
  UserX, 
  ChevronRight,
  PhoneCall,
  Target
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { apiRequest } from "../lib/utils";
import { useAuth } from "../context/AuthContext";

const callCategories = [
  {
    id: "leads-follow-ups",
    label: "Leads Follow-up",
    description: "Meta Ads / Field Marketing",
    icon: Phone,
    path: "/leads-follow-ups",
    color: "text-purple-600",
    bgColor: "bg-purple-50",
    badgeColor: "bg-purple-100 text-purple-800"
  },
  {
    id: "due",
    label: "Due",
    description: "SDR - Due for service",
    icon: Clock,
    path: "/due",
    color: "text-yellow-600",
    bgColor: "bg-yellow-50",
    badgeColor: "bg-yellow-100 text-yellow-800"
  },
  {
    id: "overdue",
    label: "Overdue",
    description: "SDR - Service overdue",
    icon: AlertCircle,
    path: "/overdue",
    color: "text-orange-600",
    bgColor: "bg-orange-50",
    badgeColor: "bg-orange-100 text-orange-800"
  },
  {
    id: "delayed",
    label: "Delayed",
    description: "SDR - Service delayed",
    icon: Calendar,
    path: "/delayed",
    color: "text-blue-600",
    bgColor: "bg-blue-50",
    badgeColor: "bg-blue-100 text-blue-800"
  },
  {
    id: "no-show",
    label: "No Show",
    description: "SDR - Did not show up",
    icon: UserX,
    path: "/no-show",
    color: "text-red-600",
    bgColor: "bg-red-50",
    badgeColor: "bg-red-100 text-red-800"
  },
  {
    id: "sdr-follow-ups",
    label: "SDR Follow-up",
    description: "Rescheduled SDR customers",
    icon: Target,
    path: "/sdr-follow-ups",
    color: "text-[#ED1C24]",
    bgColor: "bg-[#ED1C24]/10",
    badgeColor: "bg-[#ED1C24]/10 text-[#ED1C24]"
  }
];

export const CallsMenuPage = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [counts, setCounts] = useState({
    "leads-follow-ups": 0,
    "due": 0,
    "overdue": 0,
    "delayed": 0,
    "no-show": 0,
    "sdr-follow-ups": 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCounts();
  }, [user?.id]);

  const fetchCounts = async () => {
    try {
      // Fetch dashboard data to get counts
      const endpoint = isAdmin() 
        ? "/dashboard/admin" 
        : `/dashboard/cre/${user?.id}`;
      
      const data = await apiRequest(endpoint);
      
      setCounts({
        "leads-follow-ups": data.lead_follow_ups || data.funnel?.follow_up || 0,
        "due": data.due_customers || data.due_count || 0,
        "overdue": data.overdue_customers || data.overdue_count || 0,
        "delayed": data.delayed_customers || data.delayed_count || 0,
        "no-show": data.no_show_customers || data.no_show_count || 0,
        "sdr-follow-ups": data.sdr_follow_ups || 0
      });
    } catch (error) {
      console.error("Failed to fetch counts:", error);
    } finally {
      setLoading(false);
    }
  };

  const totalCalls = Object.values(counts).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-4" data-testid="calls-menu-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2.5 rounded-xl bg-[#ED1C24]/10">
          <PhoneCall className="w-6 h-6 text-[#ED1C24]" />
        </div>
        <div>
          <h1 className="font-heading text-xl font-bold">Calls</h1>
          <p className="text-sm text-muted-foreground">
            {totalCalls} customers need attention
          </p>
        </div>
      </div>

      {/* Call Categories List */}
      <Card className="bento-card" data-testid="calls-categories">
        <CardContent className="p-0">
          <div className="divide-y divide-gray-100">
            {callCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => navigate(category.path)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                data-testid={`calls-${category.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${category.bgColor}`}>
                    <category.icon className={`w-5 h-5 ${category.color}`} />
                  </div>
                  <div className="text-left">
                    <span className="font-medium block">{category.label}</span>
                    {category.description && (
                      <span className="text-xs text-muted-foreground">{category.description}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {counts[category.id] > 0 && (
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold tabular-nums ${category.badgeColor}`}>
                      {counts[category.id]}
                    </span>
                  )}
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Quick Stats Summary */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="bento-card">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold font-heading tabular-nums text-[#ED1C24]">
              {counts.due + counts.overdue}
            </p>
            <p className="text-xs text-muted-foreground">Urgent Calls</p>
          </CardContent>
        </Card>
        <Card className="bento-card">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold font-heading tabular-nums">
              {totalCalls}
            </p>
            <p className="text-xs text-muted-foreground">Total Pending</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CallsMenuPage;
